// In-memory access token with a single-flight, cross-tab-safe refresh gate.
// The access token never touches storage; only the rotated refresh token and client_id do.

import { resetClientId } from 'services/auth/client-registration';
import {
  OAUTH_CLIENT_ID,
  OAUTH_REFRESH_TOKEN,
  REFRESH_WINDOW_MS,
} from 'services/auth/constants';
import { authEvents, clearTokens, getSessionGeneration } from 'services/auth/session';
import { OAuthError, refreshTokens } from 'services/auth/token-endpoint';
import type { TokenResponse } from 'services/auth/token-endpoint';

const LOCK_NAME = 'title-oauth-refresh';
const INVALID_GRANT_RECHECK_MS = 1_000;

// Thrown (and used as the rejection while latched) when a refresh outcome is unknown or the network is down.
// Tokens are kept; consumers must treat it as a non-auth error (no clearSession, no retry).
export class DegradedSessionError extends Error {
  code = 'degraded' as const;

  constructor(cause?: unknown) {
    super('Session refresh is degraded');
    this.name = 'DegradedSessionError';
    this.cause = cause;
    Object.setPrototypeOf(this, DegradedSessionError.prototype);
  }
}

let accessToken: string | null = null;
let expiresAt = 0;
let inFlight: Promise<string> | null = null;
let latched = false;
let refreshViewStale = false;

const isFresh = (): boolean => accessToken !== null && expiresAt - REFRESH_WINDOW_MS > Date.now();

export const setMemoryToken = (token: string, expiresInSeconds: number): void => {
  accessToken = token;
  expiresAt = Date.now() + expiresInSeconds * 1000;
  latched = false;
};

export const clearMemoryToken = (): void => {
  accessToken = null;
  expiresAt = 0;
  latched = false;
};

export const getMemoryToken = (): string | null => accessToken;

export const getAccessTokenExpiry = (): number | null => (accessToken === null ? null : expiresAt);

// Returns true when a latch was actually released (the caller then runs ONE refresh).
export const releaseRefreshLatch = (): boolean => {
  const wasLatched = latched;
  latched = false;
  return wasLatched;
};

export const isRefreshTokenViewStale = (): boolean => refreshViewStale;

const delay = (ms: number) => new Promise<void>((resolve) => { setTimeout(resolve, ms); });

const readRefreshToken = (): string | null => localStorage.getItem(OAUTH_REFRESH_TOKEN);

// The server answers 400 invalid_grant for in_flight, abandoned and invalid alike, so the body is never parsed:
// re-read storage (another tab may have rotated), wait once if unchanged, retry once with a different token.
// Resolves with the tokens plus the exact refresh token that produced them.
const refreshWithRetry = async (clientId: string, used: string): Promise<{ tokens: TokenResponse; usedToken: string }> => {
  try {
    return { tokens: await refreshTokens({ clientId, refreshToken: used }), usedToken: used };
  } catch (error) {
    if (!(error instanceof OAuthError) || error.code !== 'invalid_grant') throw error;
    let next = readRefreshToken();
    if (!next || next === used) {
      await delay(INVALID_GRANT_RECHECK_MS);
      next = readRefreshToken();
    }
    if (!next || next === used) throw error;
    return { tokens: await refreshTokens({ clientId, refreshToken: next }), usedToken: next };
  }
};

// Maps a refresh failure to its side effects and the error callers see.
const handleFailure = (error: unknown, generation: number): unknown => {
  if (!(error instanceof OAuthError)) return error;
  const sessionUnchanged = generation === getSessionGeneration();
  switch (error.code) {
    case 'invalid_grant':
      if (sessionUnchanged) {
        clearTokens();
        authEvents.emit('unauthenticated');
      }
      return error;
    case 'invalid_client':
      if (sessionUnchanged) {
        resetClientId();
        authEvents.emit('unauthenticated');
      }
      return error;
    // Only invalid_grant clears the session. invalid_request keeps tokens and latches (callers see the OAuthError once).
    case 'invalid_request': {
      if (!sessionUnchanged) return error;
      latched = true;
      authEvents.emit('degraded', new DegradedSessionError(error));
      return error;
    }
    case 'rate_limited':
    case 'network':
    case 'unknown': {
      if (!sessionUnchanged) return error;
      latched = true;
      const degraded = new DegradedSessionError(error);
      authEvents.emit('degraded', degraded);
      return degraded;
    }
    default:
      return error;
  }
};

const doRefresh = async (generation: number): Promise<string> => {
  // Runs inside the lock: a logout/login while waiting for it must abort before touching storage or the network.
  if (generation !== getSessionGeneration()) {
    throw new OAuthError('invalid_grant', 'Session changed before refresh');
  }
  const refreshToken = readRefreshToken();
  const clientId = localStorage.getItem(OAUTH_CLIENT_ID);
  refreshViewStale = false;
  if (!refreshToken || !clientId) {
    authEvents.emit('unauthenticated');
    throw new OAuthError(clientId ? 'invalid_grant' : 'invalid_client', 'No stored credentials to refresh');
  }

  let tokens: TokenResponse;
  let usedToken: string;
  let rotatedToken: string;
  try {
    ({ tokens, usedToken } = await refreshWithRetry(clientId, refreshToken));
    // A rotating server always returns a new refresh token; keeping the old one risks a reuse revocation.
    if (!tokens.refresh_token) throw new OAuthError('unknown', 'Refresh response carried no refresh_token');
    rotatedToken = tokens.refresh_token;
  } catch (error) {
    throw handleFailure(error, generation);
  }

  // Logout/login during the request bumps the generation; another tab's rotation changes the stored token.
  if (generation !== getSessionGeneration() || readRefreshToken() === null) {
    throw new OAuthError('invalid_grant', 'Session changed during refresh');
  }
  if (readRefreshToken() !== usedToken) {
    throw new OAuthError('invalid_grant', 'Refresh token changed during refresh');
  }
  localStorage.setItem(OAUTH_REFRESH_TOKEN, rotatedToken);
  setMemoryToken(tokens.access_token, tokens.expires_in);
  authEvents.emit('token-changed');
  return tokens.access_token;
};

// Cross-tab exclusion via Web Locks when available; otherwise per-tab single-flight plus the re-read rule.
const withLock = async <T>(task: () => Promise<T>): Promise<T> => {
  if (typeof navigator !== 'undefined' && typeof navigator.locks?.request === 'function') {
    const result = await navigator.locks.request(LOCK_NAME, task);
    return result;
  }
  return task();
};

// Runs a task after any in-flight refresh settled, holding the cross-tab refresh lock (used by logout).
export const withRefreshLock = async <T>(task: () => Promise<T>): Promise<T> => {
  if (inFlight) await inFlight.catch(() => undefined);
  return withLock(task);
};

const startRefresh = (): Promise<string> => {
  const generation = getSessionGeneration();
  const flight = withLock(() => doRefresh(generation)).finally(() => {
    inFlight = null;
  });
  inFlight = flight;
  return flight;
};

const refreshOnce = (): Promise<string> => {
  if (inFlight) return inFlight;
  if (latched) return Promise.reject(new DegradedSessionError());
  return startRefresh();
};

export const getValidAccessToken = (): Promise<string> => {
  if (accessToken !== null && isFresh()) return Promise.resolve(accessToken);
  return refreshOnce();
};

// Refresh only when the cached token is still the one that got a 401; N concurrent 401s share one refresh.
export const forceRefresh = (failedToken: string): Promise<string> => {
  if (accessToken !== null && accessToken !== failedToken) {
    return inFlight ?? getValidAccessToken();
  }
  return refreshOnce();
};

const onStorage = (event: StorageEvent): void => {
  if (event.storageArea && event.storageArea !== localStorage) return;
  const removed = event.key === null || (event.key === OAUTH_REFRESH_TOKEN && event.newValue === null);
  if (removed) {
    clearTokens();
    authEvents.emit('unauthenticated');
    return;
  }
  // Another tab rotated: keep the valid in-memory token, just re-read the stored token on the next refresh.
  if (event.key === OAUTH_REFRESH_TOKEN) refreshViewStale = true;
};

let storageListening = false;

export const startStorageListener = (): void => {
  if (storageListening) return;
  storageListening = true;
  window.addEventListener('storage', onStorage);
};

export const stopStorageListener = (): void => {
  if (!storageListening) return;
  storageListening = false;
  window.removeEventListener('storage', onStorage);
};
